import { useState, useMemo, useContext, useEffect, useCallback } from "react";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useAuth0 } from "@auth0/auth0-react";
import DataTable from "cxfcomponents/Tables/DataTable";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import TextWithEllipsis from "layouts/pages/logs/components/TextWithEllipsis";
import StatusBadge from "cxfcomponents/StatusBadge";
import UsersCell from "layouts/Flows/components/UsersCell";
import { useUserFlows } from "hooks/useUserFlows";
import FlowMenu from "layouts/Flows/components/FlowMenu";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { useSelector } from "react-redux";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import { useNavigate } from "react-router-dom";
import UsersModalUnshare from "layouts/Flows/components/UsersModal/index3";
import UsersModal2 from "layouts/Flows/components/UsersModal/index2";
import { useRoles } from "hooks/useRoles";
import { useIsOwner } from "hooks/useIsOwner";
import { useResourceDependencies } from "hooks/useResourceDependencies";
import { permissionChecker } from "helpers/common";
import { PERMISSIONS } from "constants/portal";

export const FlowsTab = ({ integration }) => {
	const [searchName, setSearchName] = useState("");
	const { flows, handleFetchFlows } = usePortalFlows();
	const { userFlows, handleFetchUserFlows } = useUserFlows();
	const { user } = useAuth0();
	const { users } = useSelector((store) => store?.portal?.users);
	const [titleName, setTitleName] = useState("Share");
	const { userPermissions } = useContext(PermissionsContext);
	const [openUsersModal, setOpenUsersModal] = useState(false);
	const [openUsersModalUnshare, setOpenUsersModalUnshare] = useState(false);
	const [selectedUserFlow, setSelectedUserFlow] = useState(null);
	const navigate = useNavigate();
	const { roles } = useRoles(false);
	const [shareList, setShareList] = useState(null);
	const [unshareList, setUnshareList] = useState(null);
	const { reloadFlowsCount } = useSelector((store) => store?.designer?.flows);
	const isOwner = useIsOwner();
	const {
		dependencies: integrationFlowIds,
		setDependencies: setIntegrationFlowIds,
		fetchDependencies: handleFetchIntegrationFlowDependencies
	} = useResourceDependencies({
		sourceId: integration?.id,
		direction: "reverse",
		targetResourceType: "flow"
	});

	const handleReloadFlowRows = useCallback((removedFlowId) => {
		if (removedFlowId) {
			setIntegrationFlowIds((ids) =>
				ids.filter((flowId) => flowId !== removedFlowId)
			);
			handleFetchFlows();
			return;
		}

		handleFetchFlows();
		handleFetchIntegrationFlowDependencies();
	}, [handleFetchIntegrationFlowDependencies]);

	const getStatus = (flow) => {
		if (flow.trashed) return "deleted";

		if (flow.shared_status === "test") return "test";

		if (flow.steps === "") return "draft";

		return "active";
	};

	const filterFlowBySearchName = (flow) => {
		if (!searchName) return true;

		return flow.name.toLowerCase().includes(searchName);
	};

	const filterFlowByIntegration = (flow) => {
		if (!integration?.id) {
			return false;
		}

		if (!integrationFlowIds.includes(flow.id)) return false;

		if (
			isOwner ||
			permissionChecker(userPermissions, PERMISSIONS.VIEW_ANY_WORKFLOWS)
		) {
			return true;
		}

		const foundFlow = userFlows.find(
			({ flow_id, user_id }) =>
				flow_id === flow.id && user_id === user.cxfUserId
		);

		return foundFlow || flow.created_by_id === user.cxfUserId;
	};

	const tableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "flowId",
					accessor: "flowId",
					show: false
				},
				{
					Header: "icon",
					accessor: "icon",
					canGroupBy: false,
					disableSortBy: true,
					Cell: ({ value }) => (
						<SoftBox width="60px">
							<SoftBox
								component="img"
								width="100%"
								minHeight="60px"
								maxHeight="60px"
								border="0.5px solid #E6E6E6"
								borderRadius="12px"
								sx={{
									objectFit: "cover"
								}}
								src={value}
							/>
						</SoftBox>
					)
				},
				{
					Header: "name",
					accessor: "nameWithBadge",
					width: 250,
					canGroupBy: false,
					Cell: ({ value }) => (
						<SoftBox
							className="white-to-dark"
							fontSize="14px"
							fontWeight="bold"
							display="flex"
							alignItems="center"
							sx={{
								minWidth: "150px",
								maxWidth: "200px",
								whiteSpace: "normal",
								cursor: "pointer",
								wordBreak: "break-all"
							}}
							onClick={() => navigate(`/flow/${value?.id}`)}
						>
							{value?.name?.length > 24
								? value?.name?.slice(0, 24) + "..."
								: value?.name}
						</SoftBox>
					),
					sortType: (rowA, rowB, columnId) => {
						const nameA = rowA.values[columnId].name;
						const nameB = rowB.values[columnId].name;
						return nameA.localeCompare(nameB);
					}
				},
				{
					Header: "description",
					accessor: "description",
					canGroupBy: false,
					Cell: ({ value }) => {
						return (
							<SoftBox>
								<TextWithEllipsis
									value={{
										name: value,
										maxWidth: "clamp(100px, 20vw, 300px)"
									}}
								/>
							</SoftBox>
						);
					}
				},
				{
					Header: "name",
					accessor: "name",
					canGroupBy: false,
					show: false
				},
				{
					Header: "status",
					accessor: "status",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <StatusBadge status={value} />;
					}
				},
				{
					Header: "shared status",
					accessor: "sharedStatus",
					canGroupBy: true,
					expanded: true
				},
				{
					Header: "users",
					accessor: "users",
					canGroupBy: false,
					Cell: ({ value }) => {
						return <UsersCell users={value} />;
					},
					disableSortBy: true
				},
				{
					Header: "last edited",
					accessor: "lastEdited",
					width: 170,
					canGroupBy: false,
					disableSortBy: false,
					defaultSortOrder: "desc",
					sortType: (rowA, rowB, columnId) => {
						const dateA = new Date(rowA.values[columnId]);
						const dateB = new Date(rowB.values[columnId]);
						return dateA - dateB;
					}
				},
				{
					Header: "actions",
					accessor: "action",
					canGroupBy: false,
					disableSortBy: true
				}
			].map((column) => ({ ...column, disableResize: true })),
			rows: flows
				.filter(filterFlowByIntegration)
				.filter(filterFlowBySearchName)
				.map((item) => {
					const flowOwnerId = item.created_by_id;

					const flowOwner =
						users?.filter((user) =>
							flowOwnerId.includes(user.auth0_id)
						) || [];

					const filteredUsers = userFlows?.filter(
						(flow) => flow.flow_id === item.id
					);

					const sharedToUsers = filteredUsers?.map((user) => {
						return user.user_id;
					});

					const sharedUsers =
						users?.filter((user) =>
							sharedToUsers.includes(user.auth0_id)
						) || [];

					return {
						flowId: item.id,
						nameWithBadge: {
							trashed: item.trashed,
							name: item.name,
							id: item.id
						},
						name: item.name,
						sharedStatus:
							item.created_by_id === user?.cxfUserId ? "Owner" : "",
						status: `${getStatus(item)}`,
						users: [...flowOwner, ...sharedUsers],
						lastEdited:
							formatDateAndTime(
								getLocalDateTimeString(
									item.updated_date_time || item.created_date_time
								)
							) || "",
						action: (
							<FlowMenu
								item={{
									...item,
									userAccessType: item.userAccessType,
									integrationId: integration?.id
								}}
								flows={flows}
								getFlows={handleReloadFlowRows}
								userFlows={userFlows}
								setOpenUsersModal={setOpenUsersModal}
								setOpenUsersModalUnshare={setOpenUsersModalUnshare}
								setSelectedUserFlow={setSelectedUserFlow}
								forOwner
								titleName={titleName}
								setTitleName={setTitleName}
								users={users || []}
								userPermissions={userPermissions}
								onlyViewAndRemove
							/>
						),
						description: item?.description,
						icon: item.logo ? item.logo : defaultFlowIcon
					};
				})
		};
	}, [
		flows,
		userFlows,
		users,
		userPermissions,
		searchName,
		integrationFlowIds,
		integration?.id,
		isOwner,
		user?.cxfUserId,
		handleReloadFlowRows
	]);

	useEffect(() => {
		if (reloadFlowsCount > 0) {
			handleReloadFlowRows();
		}
	}, [reloadFlowsCount, handleReloadFlowRows]);

	return (
		<>
			<SoftBox>
				<SoftBox
					sx={{
						alignItems: "flex-start",
						display: "flex",
						justifyContent: "flex-start",
						padding: "12px 0 12px 40px"
					}}
				>
					<SoftInput
						size="medium"
						placeholder="Search..."
						value={searchName}
						onChange={({ currentTarget }) => {
							setSearchName(currentTarget.value);
						}}
						sx={{
							maxWidth: "380px",
							marginRight: "34px"
						}}
					/>
				</SoftBox>
				<DataTable
					table={tableData}
					entriesPerPage={{ defaultValue: 25 }}
					hiddenTopButtons
					tableView="list"
					noTopPart
					tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))"
				/>
				<UsersModal2
					users={users || []}
					roles={roles}
					userFlows={userFlows}
					selectedUserFlow={selectedUserFlow}
					openUsersModal={openUsersModal}
					setOpenUsersModal={setOpenUsersModal}
					titleName={titleName}
					shareList={shareList}
					flowView={1}
					setShareList={setShareList}
					getUserFlows={handleFetchUserFlows}
				/>
				<UsersModalUnshare
					users={users || []}
					roles={roles}
					userFlows={userFlows}
					selectedUserFlow={selectedUserFlow}
					openUsersModalUnshare={openUsersModalUnshare}
					setOpenUsersModalUnshare={setOpenUsersModalUnshare}
					titleName={titleName}
					unshareList={unshareList}
					flowView={1}
					setUnshareList={setUnshareList}
					getUserFlows={handleFetchUserFlows}
				/>
			</SoftBox>
		</>
	);
};
